import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Add, Close, Delete, Functions } from '@mui/icons-material';
import { useAtom } from 'jotai';
import { useDispatch } from 'react-redux';
import { callApi } from 'c-data-layer';
import { useCommonTranslation } from 'c-translation';
import { DiscardChangesDialog, useDiscardChangesGuard } from 'c-components';
import ApiClient from 'c-data/apiClient';
import { useAPIClientRequest } from 'c-hooks';
import type { DatasourceField, DatasourceListItem } from 'c-sdk';
import ReportingConfigBar from 'c-main/Components/Campaign/ReportingConfigBar';
import type { ReportingConfigBarHandle } from 'c-main/Components/Campaign/ReportingConfigBar';
import type {
    DashboardColor,
    DashboardComparisonSeries,
    DashboardConfig,
    DashboardFilter,
    DashboardGranularity,
    DashboardRankFrom,
    DashboardGraphType,
    DashboardStatIcon,
    DashboardStatSize,
    DashboardStatStyle,
    DashboardWidgetConfig,
    DashboardViewFilter,
} from './dashboardConfig';
import {
    atom_customDashboardConfig,
    createDefaultWidget,
    DASHBOARD_AGGREGATIONS,
    DASHBOARD_COLORS,
    DASHBOARD_COMPARISON_SERIES,
    DASHBOARD_DIMENSION_FIELDS,
    DASHBOARD_FILTER_SOURCE_DEFAULT,
    DASHBOARD_GRANULARITIES,
    DASHBOARD_GRAPH_TYPE_LABELS,
    DASHBOARD_GRAPH_TYPES,
    DASHBOARD_METRIC_FIELDS,
    DASHBOARD_STAT_ICONS,
    DASHBOARD_STAT_SIZES,
    DASHBOARD_STAT_STYLES,
    dashboardMaxDimensions,
    comparisonColorDefaults,
    expectedMetricOf,
    isAudienceField,
    isExpectedField,
    dashboardWidgetSource,
    getDefaultDashboardConfig,
    resolveWidgetTitle,
    globalFiltersForWidget,
    humanizeField,
    inferWidgetTitle,
    isTrendWidget,
    DASHBOARD_VIEW_FILTERS,
    DASHBOARD_ALL_FIELDS,
    DASHBOARD_DEFAULT_PAGE_SIZE,
    DASHBOARD_PAGE_SIZES,
    calculatedFieldsForSource,
    newDashboardWidgetId,
    normalizeDashboardConfig,
    isDefaultDashboardConfig,
    sameDashboardConfig,
} from './dashboardConfig';
import DashboardFilterRows from './DashboardFilterRows';
import DashboardWidget from './DashboardWidget';
import { DashboardStatIconTile } from './DashboardStatBody';
import DatasourceManager from './DatasourceManager';
import type { DatasourceManagerHandle } from './DatasourceManager';
import DashboardCalculatedFieldsEditor from './DashboardCalculatedFieldsEditor';
import type { DashboardCalculatedField } from './dashboardCalculations';
import {
    calcFieldOf,
    calcIdOf,
    CALC_METRIC_AGGREGATION,
    isCalcField,
    planWidgetQuery,
} from './dashboardCalculations';
import type { DashboardComparisonUnit, DashboardStatComparison } from './dashboardStat';
import { DASHBOARD_COMPARISON_UNITS, LAST_YEAR } from './dashboardStat';

// Shared height for the editor's tab panels, in px. The panels differ a lot in natural size —
// Widgets carries a preview, Global filters is a short list — and without a common height the
// dialog resizes every time you switch between them.
const TAB_PANEL_HEIGHT = 560;

type Props = {
    open?: boolean;
    onClose?: () => void;
    // Fired after a custom dashboard is saved, so the page can reveal/switch to the
    // Custom tab. The default (YTD Aggregate) tab is never edited here.
    onApplied?: () => void;
    // Render without the Dialog chrome, for embedding in a page (Admin -> Dashboard Setup).
    inline?: boolean;
    // Edit this configuration rather than the viewer's own dashboard. Supplying it with
    // onSave puts the editor in "managed" mode: nothing touches the viewer's own session
    // dashboard or their Custom tab.
    value?: DashboardConfig;
    onSave?: (config: DashboardConfig) => void;
};

// Deterministic dummy rows so the preview renders without any API calls. They have the shape
// the widget's query would come back in — calculated fields' inputs rather than the fields
// themselves — so the preview works its calculations out the way the dashboard will.
const makePreviewRows = (
    widget: DashboardWidgetConfig,
    calcs: DashboardCalculatedField[] = [],
): any[] => {
    const plan = planWidgetQuery(widget, calcs);
    const query = { ...widget, metrics: plan.metrics, dimensions: plan.dimensions };
    return makeDummyRows(query);
};

const makeDummyRows = (widget: DashboardWidgetConfig): any[] => {
    const dims = widget.dimensions.length ? widget.dimensions : [{ field: 'category' }];
    const dim1 = dims[0];
    const dim2 = dims[1];
    // dim 1 = categories; dim 2 (if present) = series, produced as a grid so a 2-dimension
    // graph shows several series per category in the preview.
    const cats = ['Sample 1', 'Sample 2', 'Sample 3', 'Sample 4'];
    const sers = dim2 ? ['Series A', 'Series B', 'Series C'] : [null];
    const rows: Record<string, any>[] = [];
    cats.forEach((c1, i) => {
        sers.forEach((c2, j) => {
            const scale = cats.length - i + (dim2 ? (sers.length - j) * 0.3 : 0);
            const expectedBase = 400000 + scale * 150000;
            const row: Record<string, any> = {};
            row[`${dim1.field}_`] = c1;
            if (dim2 && c2) row[`${dim2.field}_`] = c2;
            dims.slice(2).forEach((d, k) => {
                row[`${d.field}_`] = `${d.field} ${((i + j + k) % 3) + 1}`;
            });
            widget.metrics.forEach(m => {
                const field = m.field.toLowerCase();
                let value: number;
                if (field.startsWith('expected')) value = expectedBase;
                else if (field.includes('in_schedule')) value = Math.round(expectedBase * 0.72);
                else if (field.includes('out_of_schedule')) value = Math.round(expectedBase * 0.09);
                else if (field.includes('unbooked')) value = Math.round(expectedBase * 0.05);
                else value = 120000 + scale * 40000;
                row[`${m.field}_${m.aggregation}`] = value;
            });
            rows.push(row);
        });
    });
    return rows;
};

// A datasource field is a metric candidate when its (Glue) type is numeric; anything
// can be a dimension. Mirrors DASHBOARD_METRIC_FIELDS / DASHBOARD_DIMENSION_FIELDS.
const isNumericDatasourceType = (type: string): boolean =>
    /int|double|float|decimal|numeric|real|long/i.test(type ?? '');

// A count of 1 or more. The box keeps what is typed, so it can be cleared and retyped; only a
// whole number of at least 1 reaches the widget.
const WholeNumberField: React.FC<{
    label: string;
    value: number;
    onChange: (value: number) => void;
}> = ({ label, value, onChange }) => {
    const [text, setText] = useState(String(value));
    useEffect(() => {
        setText(current => (Number(current) === value ? current : String(value)));
    }, [value]);
    return (
        <TextField
            size="small"
            type="number"
            label={label}
            sx={{ width: 110 }}
            inputProps={{ min: 1, step: 1 }}
            value={text}
            onChange={e => {
                setText(e.target.value);
                const next = Number(e.target.value);
                if (Number.isInteger(next) && next >= 1) onChange(next);
            }}
            onBlur={() => setText(String(value))}
        />
    );
};

// The "Compare with" choices. "Same period last year" is a shift of one year, picked out
// because it is the one most often wanted.
const COMPARE_MODES = ['none', 'previousPeriod', 'lastYear', 'shift', 'dates'] as const;
type CompareMode = (typeof COMPARE_MODES)[number];
const COMPARE_DEFAULTS: Record<CompareMode, DashboardStatComparison | undefined> = {
    none: undefined,
    previousPeriod: { kind: 'previousPeriod' },
    lastYear: LAST_YEAR,
    shift: { kind: 'shift', amount: 1, unit: 'months' },
    dates: { kind: 'dates', from: '', to: '' },
};
const compareModeOf = (comparison: DashboardStatComparison | undefined): CompareMode => {
    if (!comparison) return 'none';
    if (comparison.kind !== 'shift') return comparison.kind;
    return comparison.unit === 'years' && comparison.amount === 1 ? 'lastYear' : 'shift';
};

// ---------- Widget query builder ----------
const WidgetEditor: React.FC<{
    widget: DashboardWidgetConfig;
    onChange: (next: DashboardWidgetConfig) => void;
    globalFilters: DashboardFilter[];
    datasources: DatasourceListItem[];
    // The dashboard's calculated fields for the source this widget reads.
    calculatedFields: DashboardCalculatedField[];
    // Opens the Calculated fields tab.
    onEditCalculations: () => void;
}> = ({ widget, onChange, globalFilters, datasources, calculatedFields, onEditCalculations }) => {
    const t = useCommonTranslation();
    const set = (patch: Partial<DashboardWidgetConfig>) => onChange({ ...widget, ...patch });
    const statNs = 'Pages.DashboardHome.custom.stat';
    const statId = useId();
    const compareMode = compareModeOf(widget.compareTo);
    // Calculated fields sit in the pickers next to the source's own columns, under their own
    // names: measures with the metrics, dimensions with the dimensions, both as filters.
    const calcMeasures = calculatedFields.filter(c => c.kind === 'measure');
    const calcDimensions = calculatedFields.filter(c => c.kind === 'dimension');
    const labelOf = (field: string): string => {
        if (!isCalcField(field)) return humanizeField(field, t);
        const id = calcIdOf(field);
        return (
            calculatedFields.find(c => c.id === id)?.name ??
            t('Pages.DashboardHome.custom.missingCalc')
        );
    };
    const calcMenuItems = (calcList: DashboardCalculatedField[]) =>
        calcList.length
            ? [
                  <ListSubheader key="__calc">
                      {t('Pages.DashboardHome.custom.calc.title')}
                  </ListSubheader>,
                  ...calcList.map(c => (
                      <MenuItem key={c.id} value={calcFieldOf(c.id)}>
                          <Functions fontSize="small" sx={{ mr: 1, opacity: 0.6 }} />
                          {c.name}
                      </MenuItem>
                  )),
              ]
            : [];
    const filterFields = (base: string[]) => [
        ...base,
        ...calculatedFields.map(c => calcFieldOf(c.id)),
    ];

    // When the widget is bound to a datasource, its field pickers come from that
    // datasource's synced schema (/fields) rather than the static dynamic-report fields.
    const {
        start: loadFields,
        data: fieldsResp,
        reset: resetFields,
    } = useAPIClientRequest(ApiClient.Reports.datasourceFields);
    useEffect(() => {
        resetFields();
        if (widget.datasourceId != null) {
            loadFields({ datasource: widget.datasourceId }).catch(() => undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [widget.datasourceId]);
    const datasourceFields = useMemo<DatasourceField[]>(
        () => (fieldsResp as any)?.data?.fields ?? [],
        [fieldsResp],
    );
    const usingDatasource = widget.datasourceId != null;

    // Field dropdowns, ordered alphabetically by their human label.
    const metricFields = useMemo(
        () =>
            (usingDatasource
                ? datasourceFields.filter(f => isNumericDatasourceType(f.type)).map(f => f.name)
                : [...DASHBOARD_METRIC_FIELDS]
            ).sort((a, b) => humanizeField(a, t).localeCompare(humanizeField(b, t))),
        [t, usingDatasource, datasourceFields],
    );
    const dimensionFields = useMemo(
        () =>
            (usingDatasource
                ? datasourceFields.map(f => f.name)
                : [...DASHBOARD_DIMENSION_FIELDS]
            ).sort((a, b) => humanizeField(a, t).localeCompare(humanizeField(b, t))),
        [t, usingDatasource, datasourceFields],
    );

    // What this widget inherits from the dashboard: the global filters aimed at its source.
    // Filters scoped to another source never reach it, so they aren't shown.
    const inheritedFilters = useMemo(
        () => globalFiltersForWidget(globalFilters, widget.datasourceId),
        [globalFilters, widget.datasourceId],
    );

    // Comparison needs BOTH an expected (or Geopath audience) metric and an actual one, a
    // bar-family chart, and a single dimension (with 2 dimensions the combo/series split takes
    // over instead).
    const hasExpectedMetric = expectedMetricOf(widget.metrics) != null;
    const hasActualMetric = widget.metrics.some(m => !isExpectedField(m.field));
    const colorDefaults = comparisonColorDefaults(widget);
    const comparisonSupported =
        hasExpectedMetric &&
        hasActualMetric &&
        widget.dimensions.length < 2 &&
        (widget.graphType === 'bar' ||
            widget.graphType === 'horizontalBar' ||
            widget.graphType === 'rankedBar' ||
            widget.graphType === 'line');

    // Pie and donut are the only charts where "all of it" and "share of the whole" mean
    // anything — everything else has an axis to read the value off.
    const sliceChart = widget.graphType === 'pie' || widget.graphType === 'donut';

    // "Stacked" only affects a single-dimension bar with 2+ raw metrics (it layers the
    // metrics into one bar instead of side-by-side). Anything else ignores it.
    const stackedSupported =
        (widget.graphType === 'bar' || widget.graphType === 'horizontalBar') &&
        widget.dimensions.length < 2 &&
        widget.metrics.length > 1;

    return (
        <Stack gap={2}>
            <TextField
                size="small"
                fullWidth
                label="Display name"
                value={widget.customTitle ?? ''}
                onChange={e => set({ customTitle: e.target.value })}
                placeholder={inferWidgetTitle(widget, t)}
                InputLabelProps={{ shrink: true }}
                helperText={
                    'Leave blank to name it from the query. Words the app has its own wording ' +
                    'for follow the market, typed either way round — impacts or impressions, ' +
                    'owners or partners — as do field names written with underscores, so ' +
                    '"Impacts by display_owner" reads correctly in both.'
                }
            />
            <FormControl size="small" fullWidth>
                <InputLabel>Data source</InputLabel>
                <Select
                    label="Data source"
                    value={widget.datasourceId != null ? String(widget.datasourceId) : ''}
                    onChange={e => {
                        const val = e.target.value as string;
                        const nextId = val === '' ? undefined : val;
                        if (String(nextId ?? '') === String(widget.datasourceId ?? '')) return;
                        // The field set changes with the data source, so clear the query to
                        // avoid referencing fields the new source doesn't have.
                        set({
                            datasourceId: nextId,
                            metrics: [],
                            dimensions: [],
                            filters: [],
                            sort: [],
                        });
                    }}
                >
                    <MenuItem value="">Default report (all campaigns)</MenuItem>
                    {datasources.map(ds => (
                        <MenuItem key={ds.id} value={String(ds.id)} disabled={!ds.is_queryable}>
                            {ds.name}
                            {ds.is_queryable ? '' : ' — not synced yet'}
                            {ds.is_admin_only ? ' (admin only)' : ''}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Stack direction="row" gap={2}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Graph type</InputLabel>
                    <Select
                        label="Graph type"
                        value={widget.graphType}
                        onChange={e => {
                            const graphType = e.target.value as DashboardGraphType;
                            const max = dashboardMaxDimensions(graphType);
                            set({
                                graphType,
                                dimensions: Number.isFinite(max)
                                    ? widget.dimensions.slice(0, max)
                                    : widget.dimensions,
                            });
                        }}
                    >
                        {DASHBOARD_GRAPH_TYPES.map(gt => (
                            <MenuItem key={gt} value={gt}>
                                {DASHBOARD_GRAPH_TYPE_LABELS[gt]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {widget.graphType !== 'stat' &&
                    widget.graphType !== 'deliveryComparison' &&
                    widget.graphType !== 'table' &&
                    !isTrendWidget(widget) && (
                        <FormControl size="small" sx={{ width: 110 }}>
                            <InputLabel>Show</InputLabel>
                            <Select
                                label="Show"
                                value={widget.rankFrom ?? 'top'}
                                onChange={e =>
                                    set({ rankFrom: e.target.value as DashboardRankFrom })
                                }
                            >
                                <MenuItem value="top">Top</MenuItem>
                                <MenuItem value="bottom">Bottom</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                {widget.graphType !== 'stat' &&
                    widget.graphType !== 'deliveryComparison' &&
                    widget.graphType !== 'table' &&
                    !isTrendWidget(widget) && (
                        <TextField
                            label={widget.rankFrom === 'bottom' ? 'Show bottom' : 'Show top'}
                            size="small"
                            type="number"
                            sx={{ width: 110 }}
                            inputProps={{ min: 1, max: 5 }}
                            value={widget.topN ?? 5}
                            onChange={e =>
                                set({ topN: Math.min(5, Math.max(1, Number(e.target.value) || 1)) })
                            }
                        />
                    )}
                {widget.graphType !== 'stat' &&
                    widget.graphType !== 'deliveryComparison' &&
                    widget.graphType !== 'table' &&
                    !isTrendWidget(widget) &&
                    !widget.showAllSlices && (
                        <Tooltip title="Roll everything past the ranked entries into a single Others bar or slice. Off by default — it usually dwarfs the entries the chart is about.">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!widget.showOthers}
                                        onChange={e => set({ showOthers: e.target.checked })}
                                    />
                                }
                                label="Others"
                                sx={{ whiteSpace: 'nowrap' }}
                            />
                        </Tooltip>
                    )}
                {/* A table lists every row a page at a time, so it has no top N to pick. */}
                {widget.graphType === 'table' && (
                    <FormControl size="small" sx={{ width: 140 }}>
                        <InputLabel>{t('Pages.DashboardHome.custom.rowsPerPage')}</InputLabel>
                        <Select
                            label={t('Pages.DashboardHome.custom.rowsPerPage')}
                            value={widget.pageSize ?? DASHBOARD_DEFAULT_PAGE_SIZE}
                            onChange={e => set({ pageSize: Number(e.target.value) })}
                        >
                            {DASHBOARD_PAGE_SIZES.map(size => (
                                <MenuItem key={size} value={size}>
                                    {size}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {widget.graphType === 'table' && (
                    <Tooltip title={t('Pages.DashboardHome.custom.totalsHelp')}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={widget.showTotals !== false}
                                    onChange={e => set({ showTotals: e.target.checked })}
                                />
                            }
                            label={t('Pages.DashboardHome.custom.totals')}
                            sx={{ whiteSpace: 'nowrap' }}
                        />
                    </Tooltip>
                )}
                {sliceChart && (
                    <Tooltip title="Plot every category instead of just the ranked ones, so the circle is all of the data.">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!widget.showAllSlices}
                                    onChange={e => set({ showAllSlices: e.target.checked })}
                                />
                            }
                            label="All slices"
                            sx={{ whiteSpace: 'nowrap' }}
                        />
                    </Tooltip>
                )}
                {sliceChart && (
                    <Tooltip title="Label each slice with its share of what is plotted, so the parts add up to 100%.">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!widget.percentOfTotal}
                                    onChange={e => set({ percentOfTotal: e.target.checked })}
                                />
                            }
                            label="As %"
                            sx={{ whiteSpace: 'nowrap' }}
                        />
                    </Tooltip>
                )}
                {stackedSupported && (
                    <Tooltip title="Layer the metrics on top of each other in one bar instead of drawing them side by side.">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!widget.stacked}
                                    onChange={e => set({ stacked: e.target.checked })}
                                />
                            }
                            label="Stacked"
                            sx={{ whiteSpace: 'nowrap' }}
                        />
                    </Tooltip>
                )}
            </Stack>

            {widget.graphType === 'stat' && (
                <Stack gap={1}>
                    <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel id={`${statId}-style`}>{t(`${statNs}.style`)}</InputLabel>
                            <Select
                                labelId={`${statId}-style`}
                                label={t(`${statNs}.style`)}
                                value={widget.statStyle ?? 'number'}
                                onChange={e =>
                                    set({ statStyle: e.target.value as DashboardStatStyle })
                                }
                            >
                                {DASHBOARD_STAT_STYLES.map(style => (
                                    <MenuItem key={style} value={style}>
                                        {t(`${statNs}.styles.${style}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {widget.statStyle !== 'ring' && (
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel id={`${statId}-size`}>{t(`${statNs}.size`)}</InputLabel>
                                <Select
                                    labelId={`${statId}-size`}
                                    label={t(`${statNs}.size`)}
                                    value={widget.statSize ?? 'large'}
                                    onChange={e =>
                                        set({ statSize: e.target.value as DashboardStatSize })
                                    }
                                >
                                    {DASHBOARD_STAT_SIZES.map(size => (
                                        <MenuItem key={size} value={size}>
                                            {t(`${statNs}.sizes.${size}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                            <InputLabel id={`${statId}-icon`}>{t(`${statNs}.icon`)}</InputLabel>
                            <Select
                                labelId={`${statId}-icon`}
                                label={t(`${statNs}.icon`)}
                                value={widget.statIcon ?? 'none'}
                                onChange={e =>
                                    set({ statIcon: e.target.value as DashboardStatIcon })
                                }
                                renderValue={icon => t(`${statNs}.icons.${icon}`)}
                            >
                                {DASHBOARD_STAT_ICONS.map(icon => (
                                    <MenuItem key={icon} value={icon} sx={{ gap: 1 }}>
                                        <DashboardStatIconTile
                                            icon={icon}
                                            color="currentColor"
                                            size={24}
                                        />
                                        {t(`${statNs}.icons.${icon}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!widget.signColors}
                                    onChange={e => set({ signColors: e.target.checked })}
                                />
                            }
                            label={t(`${statNs}.signColors`)}
                            sx={{ whiteSpace: 'nowrap' }}
                        />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        {t(`${statNs}.${widget.statStyle === 'ring' ? 'ringHelp' : 'help'}`)}
                    </Typography>
                    <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel id={`${statId}-compare`}>
                                {t(`${statNs}.compareWith`)}
                            </InputLabel>
                            <Select
                                labelId={`${statId}-compare`}
                                label={t(`${statNs}.compareWith`)}
                                value={compareMode}
                                onChange={e =>
                                    set({
                                        compareTo: COMPARE_DEFAULTS[e.target.value as CompareMode],
                                    })
                                }
                            >
                                {COMPARE_MODES.map(mode => (
                                    <MenuItem key={mode} value={mode}>
                                        {t(`${statNs}.compare.${mode}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {compareMode === 'shift' && widget.compareTo?.kind === 'shift' && (
                            <>
                                <WholeNumberField
                                    label={t(`${statNs}.amount`)}
                                    value={widget.compareTo.amount}
                                    onChange={amount =>
                                        widget.compareTo?.kind === 'shift' &&
                                        set({ compareTo: { ...widget.compareTo, amount } })
                                    }
                                />
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel id={`${statId}-unit`}>
                                        {t(`${statNs}.unit`)}
                                    </InputLabel>
                                    <Select
                                        labelId={`${statId}-unit`}
                                        label={t(`${statNs}.unit`)}
                                        value={widget.compareTo.unit}
                                        onChange={e =>
                                            widget.compareTo?.kind === 'shift' &&
                                            set({
                                                compareTo: {
                                                    ...widget.compareTo,
                                                    unit: e.target.value as DashboardComparisonUnit,
                                                },
                                            })
                                        }
                                    >
                                        {DASHBOARD_COMPARISON_UNITS.map(unit => (
                                            <MenuItem key={unit} value={unit}>
                                                {t(`${statNs}.units.${unit}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}
                        {widget.compareTo?.kind === 'dates' && (
                            <>
                                {(['from', 'to'] as const).map(end => (
                                    <TextField
                                        key={end}
                                        size="small"
                                        type="date"
                                        label={t(`${statNs}.${end}`)}
                                        InputLabelProps={{ shrink: true }}
                                        value={
                                            widget.compareTo?.kind === 'dates'
                                                ? widget.compareTo[end]
                                                : ''
                                        }
                                        onChange={e =>
                                            widget.compareTo?.kind === 'dates' &&
                                            set({
                                                compareTo: {
                                                    ...widget.compareTo,
                                                    [end]: e.target.value,
                                                },
                                            })
                                        }
                                    />
                                ))}
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel id={`${statId}-date-field`}>
                                        {t(`${statNs}.dateField`)}
                                    </InputLabel>
                                    <Select
                                        labelId={`${statId}-date-field`}
                                        label={t(`${statNs}.dateField`)}
                                        value={widget.compareTo.field ?? ''}
                                        displayEmpty
                                        onChange={e => {
                                            if (widget.compareTo?.kind !== 'dates') return;
                                            const { field: _dropped, ...rest } = widget.compareTo;
                                            const field = e.target.value as string;
                                            set({ compareTo: field ? { ...rest, field } : rest });
                                        }}
                                    >
                                        <MenuItem value="">
                                            {t(`${statNs}.everyDateField`)}
                                        </MenuItem>
                                        {dimensionFields.map(field => (
                                            <MenuItem key={field} value={field}>
                                                {humanizeField(field, t)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}
                    </Stack>
                    {compareMode !== 'none' && (
                        <Typography variant="caption" color="text.secondary">
                            {t(`${statNs}.compareHelp.${compareMode}`)}
                        </Typography>
                    )}
                </Stack>
            )}

            {widget.graphType === 'line' && widget.dimensions.length < 2 && (
                <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" color="text.secondary">
                        Chart controls start as
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Series</InputLabel>
                        <Select
                            label="Series"
                            value={widget.viewFilter ?? 'both'}
                            onChange={e =>
                                set({ viewFilter: e.target.value as DashboardViewFilter })
                            }
                        >
                            {DASHBOARD_VIEW_FILTERS.map(v => (
                                <MenuItem key={v} value={v}>
                                    {v === 'both'
                                        ? 'Both'
                                        : v === 'expected'
                                        ? 'Expected'
                                        : 'Delivered'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Tooltip title="Start this widget as a running total. Viewers can toggle it off from the chart toolbar.">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!widget.cumulative}
                                    onChange={e => set({ cumulative: e.target.checked })}
                                />
                            }
                            label="Accumulate"
                            sx={{ whiteSpace: 'nowrap' }}
                        />
                    </Tooltip>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!widget.showPercent}
                                onChange={e => set({ showPercent: e.target.checked })}
                            />
                        }
                        label="Percent"
                        sx={{ whiteSpace: 'nowrap' }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!widget.showNumbers}
                                onChange={e => set({ showNumbers: e.target.checked })}
                            />
                        }
                        label="Numbers"
                        sx={{ whiteSpace: 'nowrap' }}
                    />
                </Stack>
            )}

            <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap" useFlexGap>
                {/* Expected/Actual colours replace this one when comparison is on — actualColor
                    already falls back to `color`, so two controls for one line is just confusing. */}
                {!(widget.comparison && comparisonSupported) && (
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Colour</InputLabel>
                        <Select
                            label="Colour"
                            value={widget.color ?? 'primary'}
                            onChange={e => set({ color: e.target.value as DashboardColor })}
                        >
                            {DASHBOARD_COLORS.map(c => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {comparisonSupported && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!widget.comparison}
                                onChange={e => set({ comparison: e.target.checked })}
                            />
                        }
                        label={
                            isAudienceField(expectedMetricOf(widget.metrics)?.field ?? '')
                                ? 'Audited vs Actual'
                                : 'Expected vs Actual'
                        }
                        sx={{ whiteSpace: 'nowrap' }}
                    />
                )}
            </Stack>

            {widget.comparison && comparisonSupported && (
                <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Show</InputLabel>
                        <Select
                            label="Show"
                            value={widget.comparisonSeries ?? 'both'}
                            onChange={e =>
                                set({
                                    comparisonSeries: e.target.value as DashboardComparisonSeries,
                                })
                            }
                        >
                            {DASHBOARD_COMPARISON_SERIES.map(s => (
                                <MenuItem key={s} value={s}>
                                    {s === 'both'
                                        ? 'Both'
                                        : s === 'expected'
                                        ? 'Expected'
                                        : 'Actual'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {widget.graphType === 'rankedBar' &&
                        (widget.comparisonSeries ?? 'both') === 'both' && (
                            <Tooltip title="Draw expected and actual as two bars instead of one bar that runs to the larger figure.">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!widget.comparisonSideBySide}
                                            onChange={e =>
                                                set({ comparisonSideBySide: e.target.checked })
                                            }
                                        />
                                    }
                                    label="Side by side"
                                    sx={{ whiteSpace: 'nowrap' }}
                                />
                            </Tooltip>
                        )}
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Expected colour</InputLabel>
                        <Select
                            label="Expected colour"
                            value={widget.expectedColor ?? colorDefaults.expected}
                            onChange={e => set({ expectedColor: e.target.value as DashboardColor })}
                        >
                            {DASHBOARD_COLORS.map(c => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Actual colour</InputLabel>
                        <Select
                            label="Actual colour"
                            value={widget.actualColor ?? colorDefaults.actual}
                            onChange={e => set({ actualColor: e.target.value as DashboardColor })}
                        >
                            {DASHBOARD_COLORS.map(c => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            )}

            <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">Metrics</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        size="small"
                        startIcon={<Functions fontSize="small" />}
                        onClick={onEditCalculations}
                    >
                        {t('Pages.DashboardHome.custom.calc.manage')}
                    </Button>
                    <Button
                        size="small"
                        startIcon={<Add fontSize="small" />}
                        onClick={() =>
                            set({
                                metrics: [
                                    ...widget.metrics,
                                    { field: DASHBOARD_METRIC_FIELDS[0], aggregation: 'sum' },
                                ],
                            })
                        }
                    >
                        Add metric
                    </Button>
                </Stack>
                {widget.metrics.map((m, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Stack direction="row" gap={1} sx={{ mt: 1 }} key={i} alignItems="center">
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Field</InputLabel>
                            <Select
                                label="Field"
                                value={m.field}
                                onChange={e =>
                                    set({
                                        metrics: widget.metrics.map((x, j) =>
                                            j === i
                                                ? {
                                                      field: e.target.value,
                                                      aggregation: isCalcField(e.target.value)
                                                          ? CALC_METRIC_AGGREGATION
                                                          : x.aggregation,
                                                  }
                                                : x,
                                        ),
                                    })
                                }
                            >
                                {metricFields.map(f => (
                                    <MenuItem key={f} value={f}>
                                        {humanizeField(f, t)}
                                    </MenuItem>
                                ))}
                                {calcMenuItems(calcMeasures)}
                                {isCalcField(m.field) &&
                                    !calcMeasures.some(c => calcFieldOf(c.id) === m.field) && (
                                        <MenuItem value={m.field}>{labelOf(m.field)}</MenuItem>
                                    )}
                            </Select>
                        </FormControl>
                        <FormControl
                            size="small"
                            sx={{
                                width: 130,
                                visibility: isCalcField(m.field) ? 'hidden' : 'visible',
                            }}
                        >
                            <InputLabel>Aggregation</InputLabel>
                            <Select
                                label="Aggregation"
                                value={m.aggregation}
                                onChange={e =>
                                    set({
                                        metrics: widget.metrics.map((x, j) =>
                                            j === i
                                                ? { ...x, aggregation: e.target.value as any }
                                                : x,
                                        ),
                                    })
                                }
                            >
                                {DASHBOARD_AGGREGATIONS.map(a => (
                                    <MenuItem key={a} value={a}>
                                        {a}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <IconButton
                            size="small"
                            onClick={() =>
                                set({ metrics: widget.metrics.filter((_, j) => j !== i) })
                            }
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Stack>
                ))}
            </Box>

            <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">Dimensions</Typography>
                    <Button
                        size="small"
                        startIcon={<Add fontSize="small" />}
                        disabled={
                            widget.dimensions.length >= dashboardMaxDimensions(widget.graphType)
                        }
                        onClick={() =>
                            set({
                                dimensions: [
                                    ...widget.dimensions,
                                    { field: DASHBOARD_DIMENSION_FIELDS[0] },
                                ],
                            })
                        }
                    >
                        Add dimension
                    </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                    {widget.graphType === 'table'
                        ? 'Tables can use as many dimensions as you like (one column each).'
                        : dashboardMaxDimensions(widget.graphType) === 2
                        ? 'This graph supports up to 2 dimensions — the 2nd splits each category into series.'
                        : 'This graph type uses a single dimension.'}
                </Typography>
                {widget.dimensions.map((d, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Stack direction="row" gap={1} sx={{ mt: 1 }} key={i} alignItems="center">
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Field</InputLabel>
                            <Select
                                label="Field"
                                value={d.field}
                                onChange={e =>
                                    set({
                                        dimensions: widget.dimensions.map((x, j) =>
                                            j === i
                                                ? isCalcField(e.target.value)
                                                    ? { field: e.target.value }
                                                    : { ...x, field: e.target.value }
                                                : x,
                                        ),
                                    })
                                }
                            >
                                {dimensionFields.map(f => (
                                    <MenuItem key={f} value={f}>
                                        {humanizeField(f, t)}
                                    </MenuItem>
                                ))}
                                {calcMenuItems(calcDimensions)}
                                {isCalcField(d.field) &&
                                    !calcDimensions.some(c => calcFieldOf(c.id) === d.field) && (
                                        <MenuItem value={d.field}>{labelOf(d.field)}</MenuItem>
                                    )}
                            </Select>
                        </FormControl>
                        <FormControl
                            size="small"
                            sx={{
                                width: 130,
                                visibility: isCalcField(d.field) ? 'hidden' : 'visible',
                            }}
                        >
                            <InputLabel>Granularity</InputLabel>
                            <Select
                                label="Granularity"
                                value={d.granularity ?? ''}
                                onChange={e =>
                                    set({
                                        dimensions: widget.dimensions.map((x, j) =>
                                            j === i
                                                ? {
                                                      field: x.field,
                                                      ...(e.target.value
                                                          ? {
                                                                granularity: e.target
                                                                    .value as DashboardGranularity,
                                                            }
                                                          : {}),
                                                  }
                                                : x,
                                        ),
                                    })
                                }
                            >
                                <MenuItem value="">None</MenuItem>
                                {DASHBOARD_GRANULARITIES.map(g => (
                                    <MenuItem key={g} value={g}>
                                        {g}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <IconButton
                            size="small"
                            onClick={() =>
                                set({ dimensions: widget.dimensions.filter((_, j) => j !== i) })
                            }
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Stack>
                ))}
            </Box>

            <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={widget.ignoreGlobalFilters !== true}
                            onChange={e => set({ ignoreGlobalFilters: !e.target.checked })}
                        />
                    }
                    label={
                        <Typography variant="caption">
                            Apply the dashboard&apos;s filters to this widget
                        </Typography>
                    }
                />
                {widget.ignoreGlobalFilters ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Off: this widget runs on its own filters alone.
                    </Typography>
                ) : inheritedFilters.length > 0 ? (
                    <DashboardFilterRows
                        label=""
                        disabled
                        filters={inheritedFilters}
                        onChange={() => undefined}
                        t={t}
                        fields={dimensionFields}
                    />
                ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Nothing to apply — the dashboard has no filters aimed at this source.
                    </Typography>
                )}
            </Box>
            <DashboardFilterRows
                filters={widget.filters}
                onChange={filters => set({ filters })}
                t={t}
                fields={filterFields(dimensionFields)}
                labelOf={labelOf}
                source={dashboardWidgetSource(widget.datasourceId)}
                inheritedFilters={inheritedFilters}
            />

            <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">Sort</Typography>
                    <Button
                        size="small"
                        startIcon={<Add fontSize="small" />}
                        onClick={() =>
                            set({
                                sort: [
                                    ...widget.sort,
                                    { field: DASHBOARD_DIMENSION_FIELDS[0], direction: 'desc' },
                                ],
                            })
                        }
                    >
                        Add sort
                    </Button>
                </Stack>
                {widget.sort.map((s, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Stack direction="row" gap={1} sx={{ mt: 1 }} key={i} alignItems="center">
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Field</InputLabel>
                            <Select
                                label="Field"
                                value={s.field}
                                onChange={e =>
                                    set({
                                        sort: widget.sort.map((x, j) =>
                                            j === i ? { ...x, field: e.target.value } : x,
                                        ),
                                    })
                                }
                            >
                                {dimensionFields.map(field => (
                                    <MenuItem key={field} value={field}>
                                        {humanizeField(field, t)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ width: 110 }}>
                            <InputLabel>Dir</InputLabel>
                            <Select
                                label="Dir"
                                value={s.direction}
                                onChange={e =>
                                    set({
                                        sort: widget.sort.map((x, j) =>
                                            j === i
                                                ? {
                                                      ...x,
                                                      direction: e.target.value as 'asc' | 'desc',
                                                  }
                                                : x,
                                        ),
                                    })
                                }
                            >
                                <MenuItem value="asc">asc</MenuItem>
                                <MenuItem value="desc">desc</MenuItem>
                            </Select>
                        </FormControl>
                        <IconButton
                            size="small"
                            onClick={() => set({ sort: widget.sort.filter((_, j) => j !== i) })}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Stack>
                ))}
            </Box>
        </Stack>
    );
};

// Field names for several datasources at once. WidgetEditor only ever needs the one source
// its widget is bound to; the global filters need every source the dashboard uses, so those
// are fetched here and kept for the dialog's lifetime.
const useDatasourceFieldNames = (
    ids: string[],
): { names: Record<string, string[]>; numeric: Record<string, string[]> } => {
    const dispatch = useDispatch();
    const [byId, setById] = useState<Record<string, string[]>>({});
    const [numericById, setNumericById] = useState<Record<string, string[]>>({});
    const requested = useRef<Record<string, true>>({});
    const key = ids.join(',');

    useEffect(() => {
        key.split(',')
            .filter(Boolean)
            .forEach(id => {
                if (requested.current[id]) return;
                requested.current[id] = true;
                const { request } = ApiClient.Reports.datasourceFields({ datasource: id });
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore - thunk dispatch typing, mirrors useDashboardReport
                dispatch(callApi(request))
                    .then(([err, , response]: [any, any, any]) => {
                        if (!err) {
                            const fields = (response?.data?.fields ?? []) as DatasourceField[];
                            const names = fields.map(f => f.name);
                            setById(current => ({ ...current, [id]: names }));
                            setNumericById(current => ({
                                ...current,
                                [id]: fields
                                    .filter(f => isNumericDatasourceType(f.type))
                                    .map(f => f.name),
                            }));
                        }
                        return undefined;
                    })
                    .catch(() => undefined);
            });
    }, [key, dispatch]);

    return useMemo(() => ({ names: byId, numeric: numericById }), [byId, numericById]);
};

const DashboardCustomizationDialog: React.FC<Props> = ({
    open,
    onClose,
    onApplied,
    inline = false,
    value,
    onSave,
}) => {
    // Inline editors are always "open"; there is no dialog to gate on.
    const isOpen = inline || open === true;
    // Managed mode edits a supplied configuration instead of the viewer's own.
    const managed = onSave != null;
    const t = useCommonTranslation();
    const [config, setConfig] = useAtom(atom_customDashboardConfig);
    // With no custom dashboard yet, start from a copy of the built-in default set.
    const [draft, setDraft] = useState<DashboardConfig>(() =>
        normalizeDashboardConfig(config ?? getDefaultDashboardConfig()),
    );
    const [tab, setTab] = useState<'widgets' | 'global' | 'calculations' | 'datasources'>(
        'widgets',
    );
    const [selectedId, setSelectedId] = useState<string>('');
    // Sources given a Global filters section by hand, before any widget or filter uses them.
    const [extraSources, setExtraSources] = useState<string[]>([]);
    // What the editor opened with, so closing can tell whether there is anything to lose.
    const [openedWith, setOpenedWith] = useState<DashboardConfig | null>(null);
    // "Save and apply" saves under the saved-dashboard bar's "Save as…" name, so the dialog
    // holds it: seeded with the open dashboard's name, so saving updates that dashboard.
    const [saveName, setSaveName] = useState('');
    const [isSavingAndApplying, setSavingAndApplying] = useState(false);
    const [saveAndApplyFailed, setSaveAndApplyFailed] = useState(false);
    const configBarRef = useRef<ReportingConfigBarHandle>(null);
    const datasourceManagerRef = useRef<DatasourceManagerHandle>(null);

    // Saved datasources for the per-widget "Data source" picker (loaded once per open).
    const { start: loadDatasources, data: datasourcesResp } = useAPIClientRequest(
        ApiClient.Reports.datasourceList,
    );
    const datasources = useMemo<DatasourceListItem[]>(
        () => (datasourcesResp as any)?.data?.data ?? [],
        [datasourcesResp],
    );

    useEffect(() => {
        if (isOpen) {
            const normalized = normalizeDashboardConfig(
                value ?? config ?? getDefaultDashboardConfig(),
            );
            setDraft(normalized);
            setOpenedWith(normalized);
            setSaveName(normalized.name ?? '');
            setSaveAndApplyFailed(false);
            setSelectedId(normalized.widgets[0]?.id ?? '');
            setExtraSources([]);
            loadDatasources().catch(() => undefined);
        }
    }, [isOpen, value, config, loadDatasources]);

    // UL-8114 -- an edited dashboard isn't thrown away by a stray Escape or click outside.
    const isDirty = useMemo(
        () => openedWith != null && !sameDashboardConfig(draft, openedWith),
        [draft, openedWith],
    );
    const { requestClose, onDialogClose, confirmProps } = useDiscardChangesGuard(isDirty, onClose);
    const onCancel = useCallback(() => requestClose(), [requestClose]);

    const selectedWidget = useMemo(
        () => draft.widgets.find(w => w.id === selectedId),
        [draft.widgets, selectedId],
    );

    // Every source the dashboard reads — its widgets' — plus any a global filter is aimed at
    // or a section was added for. Each gets a Global filters section,
    // the default report first.
    const globalSections = useMemo(
        () => [
            ...new Set([
                DASHBOARD_FILTER_SOURCE_DEFAULT,
                ...draft.widgets.map(w => dashboardWidgetSource(w.datasourceId)),
                ...draft.globalFilters.map(f => f.source ?? DASHBOARD_FILTER_SOURCE_DEFAULT),
                ...extraSources,
            ]),
        ],
        [draft.widgets, draft.globalFilters, extraSources],
    );
    // Every section's field dropdowns need that source's schema.
    const scopedDatasourceIds = useMemo(
        () => globalSections.filter(src => src !== DASHBOARD_FILTER_SOURCE_DEFAULT).sort(),
        [globalSections],
    );
    const { names: datasourceFieldsById, numeric: datasourceNumericById } =
        useDatasourceFieldNames(scopedDatasourceIds);

    const fieldsForGlobalSource = useCallback(
        (source?: string): string[] => {
            if (!source || source === DASHBOARD_FILTER_SOURCE_DEFAULT) {
                return [...DASHBOARD_DIMENSION_FIELDS];
            }
            return datasourceFieldsById[source] ?? [];
        },
        [datasourceFieldsById],
    );
    const sourceLabel = (source: string): string =>
        source === DASHBOARD_FILTER_SOURCE_DEFAULT
            ? 'Default report (all campaigns)'
            : datasources.find(ds => String(ds.id) === source)?.name ?? `Data source ${source}`;

    // Replace one source's global filters, leaving every other source's alone.
    const setSourceFilters = (source: string, filters: DashboardFilter[]) =>
        setDraft(current => ({
            ...current,
            globalFilters: [
                ...current.globalFilters.filter(
                    f => (f.source ?? DASHBOARD_FILTER_SOURCE_DEFAULT) !== source,
                ),
                ...filters.map(f => ({ ...f, source })),
            ],
        }));

    // Calculated fields, like global filters, belong to one source each.
    const calcsForSource = (source: string): DashboardCalculatedField[] =>
        calculatedFieldsForSource(
            draft.calculatedFields,
            source === DASHBOARD_FILTER_SOURCE_DEFAULT ? undefined : source,
        );
    const setSourceCalcs = (source: string, calcs: DashboardCalculatedField[]) =>
        setDraft(current => ({
            ...current,
            calculatedFields: [
                ...(current.calculatedFields ?? []).filter(
                    c => (c.source ?? DASHBOARD_FILTER_SOURCE_DEFAULT) !== source,
                ),
                ...calcs.map(c => ({ ...c, source })),
            ],
        }));
    const allFieldsForSource = (source: string): string[] =>
        source === DASHBOARD_FILTER_SOURCE_DEFAULT
            ? DASHBOARD_ALL_FIELDS.map(f => f.field)
            : datasourceFieldsById[source] ?? [];
    const numericFieldsForSource = (source: string): string[] =>
        source === DASHBOARD_FILTER_SOURCE_DEFAULT
            ? [...DASHBOARD_METRIC_FIELDS]
            : datasourceNumericById[source] ?? [];
    const selectedCalcs = useMemo(
        () =>
            selectedWidget
                ? calculatedFieldsForSource(draft.calculatedFields, selectedWidget.datasourceId)
                : [],
        [draft.calculatedFields, selectedWidget],
    );

    // Dummy rows so the preview renders without hitting the API before Apply.
    const previewRows = useMemo(
        () => (selectedWidget ? makePreviewRows(selectedWidget, selectedCalcs) : []),
        [selectedWidget, selectedCalcs],
    );

    const updateWidget = (next: DashboardWidgetConfig) =>
        setDraft(current => ({
            ...current,
            widgets: current.widgets.map(w => (w.id === next.id ? next : w)),
        }));

    const addWidget = () => {
        const id = newDashboardWidgetId();
        setDraft(current =>
            normalizeDashboardConfig({
                ...current,
                widgets: [...current.widgets, createDefaultWidget(id)],
            }),
        );
        setSelectedId(id);
    };

    const deleteWidget = (id: string) => {
        setDraft(current =>
            normalizeDashboardConfig({
                ...current,
                widgets: current.widgets.filter(w => w.id !== id),
            }),
        );
        setSelectedId(current => (current === id ? '' : current));
    };

    const titleFor = (widgetId: string) => {
        const w = draft.widgets.find(x => x.id === widgetId);
        return w
            ? resolveWidgetTitle(
                  w,
                  t,
                  false,
                  calculatedFieldsForSource(draft.calculatedFields, w.datasourceId),
              )
            : widgetId;
    };

    // `name` is the one the dashboard was just saved under, which `draft` may not carry yet.
    const apply = (name?: string) => {
        const next = normalizeDashboardConfig(name ? { ...draft, name } : draft);
        // Managed mode hands the result back; it is not this viewer's dashboard to store.
        if (managed) {
            onSave?.(next);
            return;
        }
        // Nothing actually differs from the built-in dashboard, so there is no custom
        // dashboard to keep — don't add a Custom tab that duplicates the default one.
        if (isDefaultDashboardConfig(next)) {
            setConfig(null);
            onClose?.();
            return;
        }
        setConfig(next);
        onApplied?.();
        onClose?.();
    };

    // Data source edits first: a dashboard saved without them could read a query that isn't
    // there yet. Whatever fails stops it, with the reason on screen, and nothing is applied.
    const saveAndApply = async () => {
        setSaveAndApplyFailed(false);
        setSavingAndApplying(true);
        try {
            const datasourcesSaved = (await datasourceManagerRef.current?.saveChanges()) ?? true;
            if (!datasourcesSaved) {
                setTab('datasources');
                return;
            }
            const saved = (await configBarRef.current?.save()) ?? false;
            if (!saved) {
                setSaveAndApplyFailed(true);
                return;
            }
            apply(saveName.trim());
        } finally {
            setSavingAndApplying(false);
        }
    };

    // Discards the session's dashboard; the default dashboard is untouched either way, and any
    // dashboards assigned to this viewer remain on the Custom tab.
    const deleteCustom = () => {
        setConfig(null);
        onClose?.();
    };

    const body = (
        <>
            <Box sx={{ mb: 2 }}>
                <ReportingConfigBar
                    ref={configBarRef}
                    name={saveName}
                    onNameChange={setSaveName}
                    type="dashboard"
                    getConfig={() => JSON.stringify(normalizeDashboardConfig(draft))}
                    onLoad={(saved, name) => {
                        try {
                            // The saved record's name, not whatever the JSON carried, names
                            // the dashboard's tab — the record may have been renamed since.
                            const loaded = normalizeDashboardConfig({
                                ...JSON.parse(saved),
                                name,
                            });
                            setDraft(loaded);
                            setSaveName(name);
                            setSelectedId(loaded.widgets[0]?.id ?? '');
                            setExtraSources([]);
                        } catch {
                            // Ignore unparseable saved configs.
                        }
                    }}
                    onSaved={name => setDraft(current => ({ ...current, name }))}
                />
                {saveAndApplyFailed && (
                    <Alert
                        severity="error"
                        onClose={() => setSaveAndApplyFailed(false)}
                        sx={{ mt: 1 }}
                    >
                        {t('Pages.DashboardHome.custom.saveAndApplyFailed')}
                    </Alert>
                )}
            </Box>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab value="widgets" label="Widgets" />
                <Tab value="global" label="Global filters" />
                <Tab value="calculations" label={t('Pages.DashboardHome.custom.calc.title')} />
                <Tab value="datasources" label="Data sources" />
            </Tabs>

            <Box
                sx={{
                    height: TAB_PANEL_HEIGHT,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    pr: 1,
                }}
            >
                {tab === 'widgets' && (
                    <Stack direction="row" gap={2} alignItems="stretch">
                        <Box sx={{ width: 240, flexShrink: 0 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<Add fontSize="small" />}
                                onClick={addWidget}
                                sx={{ mb: 1 }}
                            >
                                Add widget
                            </Button>
                            <Stack gap={0.5}>
                                {draft.widgets.map(w => (
                                    <Stack
                                        key={w.id}
                                        direction="row"
                                        alignItems="center"
                                        sx={{
                                            border: th => `1px solid ${th.palette.divider}`,
                                            borderRadius: 1,
                                            bgcolor:
                                                w.id === selectedId
                                                    ? 'action.selected'
                                                    : 'background.paper',
                                        }}
                                    >
                                        <Button
                                            onClick={() => setSelectedId(w.id)}
                                            sx={{
                                                flex: 1,
                                                justifyContent: 'flex-start',
                                                textTransform: 'none',
                                                minWidth: 0,
                                            }}
                                        >
                                            <Typography variant="body2" noWrap>
                                                {titleFor(w.id)}
                                            </Typography>
                                        </Button>
                                        <IconButton
                                            size="small"
                                            onClick={() => deleteWidget(w.id)}
                                            sx={{ mr: 0.5 }}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {selectedWidget ? (
                                <Stack gap={2}>
                                    <Box
                                        sx={{
                                            border: th => `1px solid ${th.palette.divider}`,
                                            borderRadius: 1,
                                            p: 1,
                                            bgcolor: 'action.hover',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mb: 0.5 }}
                                        >
                                            Preview
                                        </Typography>
                                        <DashboardWidget
                                            key={selectedWidget.id}
                                            config={selectedWidget}
                                            previewData={previewRows}
                                            calculatedFields={selectedCalcs}
                                            height={220}
                                        />
                                    </Box>
                                    <WidgetEditor
                                        widget={selectedWidget}
                                        onChange={updateWidget}
                                        globalFilters={draft.globalFilters}
                                        datasources={datasources}
                                        calculatedFields={selectedCalcs}
                                        onEditCalculations={() => setTab('calculations')}
                                    />
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Select a widget to edit, or add one.
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                )}

                {tab === 'global' && (
                    <Stack gap={1.5} sx={{ maxWidth: 760 }}>
                        <Typography variant="body2" color="text.secondary">
                            Everything listed here is applied, on top of each widget&apos;s own
                            filters, to every widget reading that source. Sources don&apos;t share
                            fields, so each has its own section.
                        </Typography>
                        {globalSections.map(source => {
                            const filters = globalFiltersForWidget(
                                draft.globalFilters,
                                source === DASHBOARD_FILTER_SOURCE_DEFAULT ? undefined : source,
                            );
                            const readers = draft.widgets.filter(
                                w => dashboardWidgetSource(w.datasourceId) === source,
                            );
                            const optedOut = readers.filter(w => w.ignoreGlobalFilters).length;
                            const removable =
                                extraSources.includes(source) &&
                                filters.length === 0 &&
                                readers.length === 0;
                            const reach = [
                                readers.length > 0 &&
                                    `${readers.length} widget${readers.length === 1 ? '' : 's'}${
                                        optedOut > 0 ? ` (${optedOut} opted out)` : ''
                                    }`,
                            ].filter(Boolean);
                            return (
                                <Box
                                    key={source}
                                    sx={{
                                        p: 1.5,
                                        border: th => `1px solid ${th.palette.divider}`,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <Typography variant="subtitle2">
                                            {sourceLabel(source)}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={`${filters.length} active`}
                                            color={filters.length > 0 ? 'primary' : 'default'}
                                            variant="outlined"
                                        />
                                        <Box sx={{ flex: 1 }} />
                                        {removable && (
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setExtraSources(current =>
                                                        current.filter(x => x !== source),
                                                    )
                                                }
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Stack>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mb: 0.5 }}
                                    >
                                        {reach.length > 0
                                            ? `Applies to ${reach.join(' and ')}.`
                                            : 'Nothing on this dashboard reads this source yet.'}
                                    </Typography>
                                    <DashboardFilterRows
                                        label=""
                                        filters={filters}
                                        onChange={next => setSourceFilters(source, next)}
                                        t={t}
                                        fields={fieldsForGlobalSource(source)}
                                        source={source}
                                    />
                                </Box>
                            );
                        })}
                        {datasources.some(ds => !globalSections.includes(String(ds.id))) && (
                            <FormControl size="small" sx={{ maxWidth: 360 }}>
                                <InputLabel>Add filters for another data source</InputLabel>
                                <Select
                                    label="Add filters for another data source"
                                    value=""
                                    onChange={e => {
                                        const source = e.target.value as string;
                                        if (source) {
                                            setExtraSources(current => [...current, source]);
                                        }
                                    }}
                                >
                                    {datasources
                                        .filter(ds => !globalSections.includes(String(ds.id)))
                                        .map(ds => (
                                            <MenuItem key={ds.id} value={String(ds.id)}>
                                                {ds.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </Stack>
                )}

                {tab === 'calculations' && (
                    <Stack gap={1.5} sx={{ maxWidth: 860 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('Pages.DashboardHome.custom.calc.intro')}
                        </Typography>
                        {globalSections.map(source => (
                            <Box
                                key={source}
                                sx={{
                                    p: 1.5,
                                    border: th => `1px solid ${th.palette.divider}`,
                                    borderRadius: 1,
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {sourceLabel(source)}
                                </Typography>
                                <DashboardCalculatedFieldsEditor
                                    source={source}
                                    calcs={calcsForSource(source)}
                                    onChange={next => setSourceCalcs(source, next)}
                                    fields={allFieldsForSource(source)}
                                    numericFields={numericFieldsForSource(source)}
                                />
                            </Box>
                        ))}
                    </Stack>
                )}

                {/* Kept mounted, so an edit survives a look at another tab until it is saved
                    here or by "Save and apply". */}
                <Box hidden={tab !== 'datasources'}>
                    <DatasourceManager ref={datasourceManagerRef} />
                </Box>
            </Box>
        </>
    );

    const actions = (
        <>
            <Button
                onClick={() => {
                    setDraft(getDefaultDashboardConfig());
                    setExtraSources([]);
                }}
                color="inherit"
            >
                Reset
            </Button>
            {/* Only the viewer's own dashboard can be discarded from here; a saved
                configuration is removed from the Dashboard Setup list instead. */}
            {!managed && config && (
                <Button onClick={deleteCustom} color="error">
                    Discard
                </Button>
            )}
            <Box sx={{ flex: 1 }} />
            {onClose != null && (
                <Button onClick={onCancel} color="inherit">
                    Cancel
                </Button>
            )}
            {!managed && (
                <Tooltip
                    title={
                        saveName.trim() ? '' : t('Pages.DashboardHome.custom.saveAndApplyNeedsName')
                    }
                >
                    <span>
                        <Button
                            onClick={saveAndApply}
                            variant="outlined"
                            disabled={!saveName.trim() || isSavingAndApplying}
                        >
                            {t('Pages.DashboardHome.custom.saveAndApply')}
                        </Button>
                    </span>
                </Tooltip>
            )}
            <Button onClick={() => apply()} variant="contained" disabled={isSavingAndApplying}>
                {managed ? 'Save' : 'Apply'}
            </Button>
        </>
    );

    if (inline) {
        return (
            <Box>
                {body}
                <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 3 }}>
                    {actions}
                </Stack>
                <DiscardChangesDialog {...confirmProps} />
            </Box>
        );
    }

    return (
        <Dialog open={isOpen} onClose={onDialogClose} maxWidth="lg" fullWidth>
            <DiscardChangesDialog {...confirmProps} />
            <DialogTitle>
                {config ? 'Customise Dashboard' : 'Create Custom Dashboard'}
                {draft.name && (
                    <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
                        — {draft.name}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent dividers>{body}</DialogContent>
            <DialogActions>{actions}</DialogActions>
        </Dialog>
    );
};

export default DashboardCustomizationDialog;
